// stores/useStockSymbolsStore.ts
import { defineStore } from "pinia";
import axios from "axios";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc"; // often timezone plugin requires UTC as well
import { ref, computed, nextTick, watch } from "vue"; // Import ref, computed, and watch
import localforage from "localforage";
import MD5 from "crypto-js/md5";

import type { MostUpToDateDataType } from "../utils/types";

dayjs.extend(utc); // use UTC plugin
dayjs.extend(timezone); // use Timezone plugin
dayjs.locale("en");
dayjs.tz.setDefault("America/New_York");

/**
 * Interface representing a stock item with its properties
 * @interface StockItem
 * @property {string} SYMBOL - The stock symbol/ticker
 * @property {string} NAME - The company name
 * @property {number} PC_CHG - The percentage change in price
 * @property {number} NET_CHG - The net change in price
 * @property {number} BID - The current bid price
 * @property {any} [key: string] - Additional dynamic properties
 */
export interface StockItem {
	SYMBOL: string;
	NAME: string;
	PC_CHG: number;
	NET_CHG: number;
	BID: number;
	[key: string]: any;
}

type ReferencedStocksInfo = {
	[key: string]: MostUpToDateDataType;
};

// Define the localforage instance outside the store definition
// so it's created only once when the module is imported.
const stockCache = localforage.createInstance({
	name: "stockDB",
	storeName: "indexSymbols",
	driver: localforage.INDEXEDDB // Ensure INDEXEDDB is available, or provide fallbacks
});

// Define the store using the setup function syntax
export const useStocksStore = defineStore("stocks", () => {
	// State: Define reactive state using ref or reactive
	const symbols_list = ref<any[]>([]); // Use ref for reactive primitive/array/object
	const lastUpdated = ref<number | null>(null);
	const isLoading = ref(false);
	const error = ref<Error | null>(null);
	const most_active_stocks = ref<{ [key: string]: StockItem }>({});
	const top_gainers_stocks = ref<StockItem[]>([]);
	const top_losers_stocks = ref<StockItem[]>([]);
	const stocks = ref<ReferencedStocksInfo>();
	const pollingInterval = ref<NodeJS.Timeout | null>(null);

	const runtimeConfig = useRuntimeConfig();
	const serverUrls = runtimeConfig.public.server_urls;

	const stock_subscribed_symbols = ref(new Set<string>());
	const stock_subscriptions = reactive(new Map<string, StockItem>());

	// Constants can be defined within the setup scope
	const CACHE_DURATION_MS = 1000 * 60 * 60; // 1 hour

	// Getters: Define computed properties
	const isDataStale = computed(() => {
		// Access state refs using .value
		return (
			lastUpdated.value === null ||
			Date.now() - lastUpdated.value > CACHE_DURATION_MS
		);
	});

	const createSecretToken = () => {
		const date = dayjs(Date.now())
			.tz("America/New_York")
			.format("ddd, D MMM YYYY");
		const secretToken = MD5(date).toString();
		return secretToken;
	};
	// Actions: Define functions directly inside the setup function
	// Define refresh first as init calls it
	const refresh = async () => {
		isLoading.value = true; // Access state refs with .value
		error.value = null; // Access state refs with .value

		try {
			const res = await axios.get("/api/symbols-list");
			console.log("[Symbols]:", res.data);
			const fetchedData = res.data; // Use a different variable name to avoid confusion

			const lastUpdatedTimestamp = Date.now();
			// Update state refs using .value
			symbols_list.value = fetchedData;
			lastUpdated.value = lastUpdatedTimestamp;

			console.log("Stock symbols fetched successfully. Updating cache.");
			// Update local cache
			await stockCache.setItem("symbols", fetchedData); // Cache the data from the ref
			await stockCache.setItem("symbols_last_updated", lastUpdatedTimestamp); // Cache the timestamp from the ref
		} catch (err) {
			error.value = err as Error; // Access state refs with .value
			console.error("Error during stock symbols refresh:", err.response.data);
		} finally {
			isLoading.value = false; // Access state refs with .value
		}
	};

	const init = async () => {
		// Prevent multiple initializations if already loading
		if (isLoading.value) {
			// Access state refs with .value
			console.log("Stock symbols init called while already loading, skipping.");
			return;
		}

		isLoading.value = true; // Access state refs with .value
		error.value = null; // Access state refs with .value

		try {
			console.log("Stock symbols init: Checking cache...");
			const cached = await stockCache.getItem<any[]>("symbols");
			const timestamp = await stockCache.getItem<number>("symbols_last_updated");

			// Check if cached data exists AND the timestamp is not null AND the cache is still valid
			if (
				cached &&
				timestamp !== null &&
				Date.now() - timestamp < CACHE_DURATION_MS
			) {
				symbols_list.value = cached; // Update state ref from cache
				lastUpdated.value = timestamp; // Update state ref from cache timestamp
				console.log("Stock symbols loaded from cache.");
			} else {
				console.log(
					"Stock symbols cache is missing or stale, fetching fresh data."
				);
				await refresh(); // Call the refresh action (defined above)
			}
		} catch (err) {
			error.value = err as Error; // Access state refs with .value
			console.error("Error during stock symbols init:", err);
		} finally {
			isLoading.value = false; // Access state refs with .value
		}
	};

	function setMostActiveStocks(
		stocks: { [key: string]: StockItem } | StockItem[]
	) {
		// Convert array to object if needed (for backward compatibility)
		const stocksObject = Array.isArray(stocks)
			? stocks.reduce(
					(acc, stock) => {
						acc[stock.SYMBOL] = stock;
						return acc;
					},
					{} as { [key: string]: StockItem }
				)
			: stocks;

		// Only update if data has changed
		const currentStocks = most_active_stocks.value;
		const hasChanged =
			Object.keys(stocksObject).some((symbol) => {
				const currentStock = currentStocks[symbol];
				const newStock = stocksObject[symbol];

				if (!currentStock) {
					return true; // New stock
				}

				// Compare key properties that indicate real changes
				return (
					currentStock.BID !== newStock.BID ||
					currentStock.PC_CHG !== newStock.PC_CHG ||
					currentStock.NET_CHG !== newStock.NET_CHG ||
					currentStock.NAME !== newStock.NAME
				);
			}) || Object.keys(currentStocks).length !== Object.keys(stocksObject).length;

		if (hasChanged) {
			most_active_stocks.value = stocksObject;
		} else {
			console.log("📊 Most active stocks - no changes detected");
		}
	}

	function updateMostActiveStocksCurrentData(
		currentDataStocks: { [key: string]: StockItem } | StockItem[]
	) {
		// Convert array to object if needed (for backward compatibility)
		const currentDataObject = Array.isArray(currentDataStocks)
			? currentDataStocks.reduce(
					(acc, stock) => {
						acc[stock.SYMBOL] = stock;
						return acc;
					},
					{} as { [key: string]: StockItem }
				)
			: currentDataStocks;

		// Get existing stocks with historic data
		const existingStocks = most_active_stocks.value;

		// Only update if current data has changed
		const hasChanged = Object.keys(currentDataObject).some((symbol) => {
			const existingStock = existingStocks[symbol];
			const newCurrentData = currentDataObject[symbol];

			if (!existingStock) {
				return true; // New stock
			}

			// Compare only current data properties (not historic data)
			return (
				existingStock.BID !== newCurrentData.BID ||
				existingStock.PC_CHG !== newCurrentData.PC_CHG ||
				existingStock.NET_CHG !== newCurrentData.NET_CHG ||
				existingStock.NAME !== newCurrentData.NAME
			);
		});

		if (hasChanged) {
			// Update stocks by preserving historic data (prices, labels) and updating current data
			const updatedStocks = { ...existingStocks };

			Object.keys(currentDataObject).forEach((symbol) => {
				const existingStock = updatedStocks[symbol];
				const newCurrentData = currentDataObject[symbol];

				// Preserve historic data if it exists, update current data
				updatedStocks[symbol] = {
					...newCurrentData, // New current data
					prices: existingStock?.prices || [], // Preserve existing prices
					labels: existingStock?.labels || [] // Preserve existing labels
				};
			});

			most_active_stocks.value = updatedStocks;
			console.log(
				"📊 Most active stocks current data updated - preserving charts"
			);
		} else {
			console.log("📊 Most active stocks current data - no changes detected");
		}
	}

	function setTopGainerStocks(stocks: StockItem[]) {
		top_gainers_stocks.value = [...stocks];
	}

	function setTopLoserStocks(stocks: StockItem[]) {
		top_losers_stocks.value = [...stocks];
	}

	const getMostActiveStocks = computed(() => {
		if (
			!most_active_stocks.value ||
			Object.keys(most_active_stocks.value).length === 0
		) {
			return [];
		}
		return Object.values(most_active_stocks.value).map((item) => {
			const prices = item.HISTORIC_DATA?.map((data: any) => data.close) || [];
			const labels = item.HISTORIC_DATA?.map((data: any) => data.date) || [];

			return {
				...item,
				prices,
				labels
			};
		});
	});

	const getTopGainerStocks = computed(() => {
		if (!top_gainers_stocks.value || top_gainers_stocks.value.length === 0) {
			return [];
		}
		return top_gainers_stocks.value.map((item) => {
			const prices = item.HISTORIC_DATA?.map((data: any) => data.close) || [];
			const labels = item.HISTORIC_DATA?.map((data: any) => data.date) || [];

			return {
				...item,
				prices,
				labels
			};
		});
	});

	const getTopLoserStocks = computed(() => {
		if (!top_losers_stocks.value || top_losers_stocks.value.length === 0) {
			return [];
		}
		return top_losers_stocks.value.map((item) => {
			const prices = item.HISTORIC_DATA?.map((data: any) => data.close) || [];
			const labels = item.HISTORIC_DATA?.map((data: any) => data.date) || [];

			return {
				...item,
				prices,
				labels
			};
		});
	});

	const fetchStocksData = async (
		symbols: string[],
		dontDoYtd: boolean = false
	) => {
		const secretToken = createSecretToken();

		const headers = {
			secretToken,
			Accept: "application/json"
		};

		// let stocksDict: ReferencedStocksInfo = {};

		let payload: MostUpToDateDataType[] = [];

		try {
			const symbols_filter = symbols.map((symbol) => `'${symbol}'`).join(",");
			const data = {
				filter: `SYMBOL IN (${symbols_filter})`,
				limit: 1,
				fields:
					"BID,PE_RATIO, VOLUME, TRADES, FLOAT, SHORT_INTEREST,SHORT_INTEREST_RATIO, SYMBOL, NAME, LAST,LAST_TRADE_TIME, NET_CHG, PC_CHG, MARKET_CAP, HIGH_52W, LOW_52W, LOW, HIGH, DAY_RANGE, MARKET, YTD_CHG, YTD_PCT",
				prevDay: dayjs
					.tz(Date.now(), "America/New_York")
					.isBefore(
						dayjs.tz(Date.now(), "America/New_York").hour(8).minute(0).second(0)
					)
			};

			const res = await axios.post(
				serverUrls["/httpQueryBasedFilterServer"],
				data,
				{
					headers
				}
			);

			if (res.status === 200) {
				let stocks_res = res.data;

				if (!dontDoYtd) {
					const ytd_res = await axios.get("/api/stock-historic", {
						params: {
							symbols: symbols.join(","),
							ytd: true
						}
					});

					if (ytd_res.status === 200) {
						const data = ytd_res.data.results;

						const new_stuff = stocks_res.map((stock: MostUpToDateDataType) => {
							const ytd_data = data[stock.SYMBOL];
							return {
								...stock,
								...ytd_data
							};
						});

						stocks_res = new_stuff;
					}
				}

				payload = stocks_res;
			}
		} catch (error) {
			console.error("Error fetching stocks data:", error);
			return { data: [] };
		}
		await nextTick();

		if (stocks.value && Object.keys(stocks.value).length > 0) {
			payload = payload.map((stock: MostUpToDateDataType) => {
				const stock_value = stocks.value?.[stock.SYMBOL];

				return {
					...stock,
					YTD_CHG: stock_value?.YTD_CHG || 0,
					YTD_PCT: stock_value?.YTD_PCT || 0
				};
			});
		}

		const finalPayload = payload.reduce(
			(acc: Record<string, MostUpToDateDataType>, stock: MostUpToDateDataType) => {
				acc[stock.SYMBOL] = stock;
				return acc;
			},
			{}
		);

		stocks.value = { ...finalPayload };
	};

	const fetchStocks = async (symbols: string[]) => {
		// Clear any existing interval first
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}

		await fetchStocksData(symbols);

		pollingInterval.value = setInterval(() => {
			fetchStocksData(symbols, true);
		}, 1000 * 10); // Every 10 seconds
	};

	const cleanup = () => {
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}
		// Clear all stocks-related data
		stocks.value = undefined;
		stock_subscribed_symbols.value.clear();
		stock_subscriptions.clear();
	};

	// Symbol tracking methods
	const startTrackingSymbols = async (symbols: string[]) => {
		// Clear any existing interval first
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}

		// Start tracking the new symbols
		await fetchStocksData(symbols);

		// Set up polling interval for real-time updates
		// pollingInterval.value = setInterval(() => {
		// 	fetchStocksData(symbols, true);
		// }, 1000 * 10); // Every 10 seconds
	};

	const stopTrackingSymbols = () => {
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}

		// Clear all stocks-related data
		stocks.value = undefined;
		stock_subscribed_symbols.value.clear();
		stock_subscriptions.clear();
	};

	// YTD data fetching method
	const fetchYtdDataForStocks = async () => {
		if (!stocks.value || Object.keys(stocks.value).length === 0) {
			return;
		}

		try {
			const symbols = Object.keys(stocks.value);
			const ytdPromises = symbols.map(async (symbol) => {
				try {
					const response = await axios.get(`/api/stock-historic`, {
						params: {
							symbol,
							range: "1D",
							ytd: "true"
						}
					});

					if (
						response.data &&
						response.data.results &&
						response.data.results[symbol]
					) {
						const ytdData = response.data.results[symbol];
						return {
							symbol,
							YTD_CHG: ytdData.YTD_CHG || 0,
							YTD_PCT: ytdData.YTD_PCT || 0
						};
					}
					return null;
				} catch (error) {
					console.error(
						`[StocksStore] Error fetching YTD data for ${symbol}:`,
						error
					);
					return null;
				}
			});

			const ytdResults = await Promise.all(ytdPromises);
			const validYtdData = ytdResults.filter(Boolean);

			// Update stocks with YTD data
			validYtdData.forEach((ytdData) => {
				if (ytdData && stocks.value && stocks.value[ytdData.symbol]) {
					stocks.value[ytdData.symbol] = {
						...stocks.value[ytdData.symbol],
						YTD_CHG: ytdData.YTD_CHG,
						YTD_PCT: ytdData.YTD_PCT
					};
				}
			});
		} catch (error) {
			console.error("[StocksStore] Error fetching YTD data:", error);
		}
	};

	// Stock subscriptions management methods
	/**
	 * Add a stock to the subscriptions map
	 * @param stockItem - The stock item to add (SYMBOL will be used as the key)
	 */
	const addStockSubscription = (stockItem: StockItem) => {
		stock_subscriptions.set(stockItem.SYMBOL, stockItem);
		console.log(`[StocksStore] Added subscription for ${stockItem.SYMBOL}`);
	};

	/**
	 * Add multiple stocks to the subscriptions map
	 * @param stockItems - Array of stock items to add
	 */
	const addMultipleStockSubscriptions = (stockItems: StockItem[]) => {
		stockItems.forEach((stockItem) => {
			stock_subscriptions.set(stockItem.SYMBOL, stockItem);
		});
		console.log(`[StocksStore] Added ${stockItems.length} stock subscriptions`);
	};

	/**
	 * Update a specific stock in the subscriptions map
	 * @param symbol - The stock symbol to update
	 * @param updates - Partial stock data to update
	 */
	const updateStockSubscription = (
		symbol: string,
		updates: Partial<StockItem>
	) => {
		const existingStock = stock_subscriptions.get(symbol);
		if (existingStock) {
			const updatedStock = { ...existingStock, ...updates };
			stock_subscriptions.set(symbol, updatedStock);
			console.log(`[StocksStore] Updated subscription for ${symbol}`);
		} else {
			console.warn(
				`[StocksStore] Cannot update ${symbol}: not found in subscriptions`
			);
		}
	};

	/**
	 * Remove a stock subscription
	 * @param symbol - The stock symbol to remove
	 */
	const removeStockSubscription = (symbol: string) => {
		const removed = stock_subscriptions.delete(symbol);
		if (removed) {
			console.log(`[StocksStore] Removed subscription for ${symbol}`);
		} else {
			console.warn(
				`[StocksStore] Cannot remove ${symbol}: not found in subscriptions`
			);
		}
		return removed;
	};

	/**
	 * Clear all stock subscriptions
	 */
	const clearStockSubscriptions = () => {
		const count = stock_subscriptions.size;
		stock_subscriptions.clear();
		console.log(`[StocksStore] Cleared ${count} stock subscriptions`);
	};

	/**
	 * Check if a stock is subscribed
	 * @param symbol - The stock symbol to check
	 */
	const hasStockSubscription = (symbol: string): boolean => {
		return stock_subscriptions.has(symbol);
	};

	/**
	 * Get a specific stock subscription
	 * @param symbol - The stock symbol to get
	 */
	const getStockSubscription = (symbol: string): StockItem | undefined => {
		return stock_subscriptions.get(symbol);
	};

	/**
	 * Extract symbols from odd indices of stock_pairs array and save to stock_subscribed_symbols set
	 * @param stock_pairs - Array where symbols are at odd indices (1, 3, 5, etc.)
	 */
	const setSubscribedSymbolsFromPairs = (stock_pairs: string[]) => {
		// Clear existing symbols
		stock_subscribed_symbols.value.clear();

		// Extract symbols from odd indices and add to set
		stock_pairs.forEach((item, index) => {
			if (index % 2 === 1) {
				// Odd indices (1, 3, 5, etc.)
				stock_subscribed_symbols.value.add(item);
			}
		});

		console.log(
			`[StocksStore] Added ${stock_subscribed_symbols.value.size} symbols from stock pairs`
		);
	};

	/**
	 * Add symbols from stock_pairs array to existing stock_subscribed_symbols set (without clearing)
	 * @param stock_pairs - Array where symbols are at odd indices (1, 3, 5, etc.)
	 */
	const addSubscribedSymbolsFromPairs = (stock_pairs: string[]) => {
		// Extract symbols from odd indices and add to existing set
		stock_pairs.forEach((item, index) => {
			if (index % 2 === 1) {
				// Odd indices (1, 3, 5, etc.)
				stock_subscribed_symbols.value.add(item);
			}
		});
	};

	// Computed getters for stock subscriptions
	const getStockSubscriptions = computed(() => {
		return Array.from(stock_subscriptions.values());
	});

	const getStockSubscriptionsMap = computed(() => {
		return stock_subscriptions;
	});

	const getStockSubscriptionsCount = computed(() => {
		return stock_subscriptions.size;
	});

	const getSubscribedSymbols = computed(() => {
		return Array.from(stock_subscriptions.keys());
	});

	// Computed getters for stock_subscribed_symbols set
	const getSubscribedSymbolsSet = computed(() => {
		return stock_subscribed_symbols.value;
	});

	const getSubscribedSymbolsArray = computed(() => {
		return Array.from(stock_subscribed_symbols.value);
	});

	const getSubscribedSymbolsCount = computed(() => {
		return stock_subscribed_symbols.value.size;
	});

	// Watch the computed array instead of the Set directly
	watch(getSubscribedSymbolsArray, async (newVal, oldVal) => {
		if (process.client && newVal !== oldVal) {
			// Filter out symbols containing special characters, spaces, or empty strings
			const validSymbols = newVal.filter(
				(symbol) =>
					symbol &&
					symbol.trim() !== "" &&
					!/[.+\s]/.test(symbol) &&
					/^[A-Za-z0-9-]+$/.test(symbol)
			);

			if (validSymbols.length === 0) {
				console.log(
					"[StocksStore] No valid symbols to fetch data for after filtering"
				);
				return;
			}

			const { data: stock_response, status } = await axios.get(
				`/api/current-stock-data`,
				{
					params: {
						symbol: validSymbols.join(",")
					}
				}
			);

			if (status === 200) {
				addMultipleStockSubscriptions(stock_response.data);
			}
		}
	});

	// Return everything you want to expose from the store
	return {
		// State (exposed as refs)
		stocks,
		symbols_list,
		lastUpdated,
		isLoading,
		error,
		stock_subscribed_symbols,
		// Getters (exposed as computed refs)
		isDataStale,
		// Actions (exposed as functions)
		init,
		refresh,
		most_active_stocks,
		setMostActiveStocks,
		updateMostActiveStocksCurrentData,
		getMostActiveStocks,
		top_gainers_stocks,
		setTopGainerStocks,
		getTopGainerStocks,
		top_losers_stocks,
		setTopLoserStocks,
		getTopLoserStocks,
		fetchStocks,
		cleanup,
		fetchStocksData,
		startTrackingSymbols,
		stopTrackingSymbols,
		fetchYtdDataForStocks,
		// Stock subscriptions methods
		addStockSubscription,
		addMultipleStockSubscriptions,
		updateStockSubscription,
		removeStockSubscription,
		clearStockSubscriptions,
		hasStockSubscription,
		getStockSubscription,
		// Stock pairs methods
		setSubscribedSymbolsFromPairs,
		addSubscribedSymbolsFromPairs,
		// Stock subscriptions getters
		getStockSubscriptions,
		getStockSubscriptionsMap,
		getStockSubscriptionsCount,
		getSubscribedSymbols,
		// Stock subscribed symbols getters
		getSubscribedSymbolsSet,
		getSubscribedSymbolsArray,
		getSubscribedSymbolsCount,
		stock_subscriptions
	};
});
